import ls from 'localstorage-slim';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cancelLeaseApplication } from '../../../../api/instant-lease-api';
import { setLoaderModal, setNotification } from '../../../../redux/cache-slice';
import {
  clearCarData,
  fetchLeaseApps,
} from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import { getText } from '../../../../utils/getter';

const STORAGE_KEYS_TO_CLEAR = [
  'carData',
  'ar',
  'ps',
  'bo',
  'tr',
  'loa',
  'pcms',
  'companyName',
  'companyZip',
];

/**
 * Selects one lease application: cancels all other open applications,
 * refreshes the list, clears the cached car data and navigates to the chosen
 * application. `isSelecting` guards against double submits.
 */
export const useSelectApplication = () => {
  const { leaseApplications } = useSelector(
    (state: RootState) => state.leaseApp,
  );
  const { sequence } = useSelector((state: RootState) => state.sequence);
  const { accessToken } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isSelecting, setIsSelecting] = useState(false);

  const selectApplication = useCallback(
    async (selectedUUID: string) => {
      if (isSelecting) return;
      setIsSelecting(true);
      const loaderText = getText('car_selection_loader');
      try {
        await Promise.all(
          leaseApplications
            .filter((app) => app.uuid !== selectedUUID)
            .map((app) => cancelLeaseApplication(accessToken, app.uuid)),
        );
        dispatch(setLoaderModal({ visibility: true, text: loaderText }));
        await dispatch(fetchLeaseApps({ accessToken }));
        dispatch(clearCarData());
        STORAGE_KEYS_TO_CLEAR.forEach((key) => ls.remove(key));
        navigate(`/lease-application/${selectedUUID}/${sequence + 1}`);
      } catch (e) {
        dispatch(setLoaderModal({ visibility: false, text: loaderText }));
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: true,
            notificationTitle: getText('car_selection_error_title'),
            notificationBody: getText('error_unable_to_process_data'),
          }),
        );
        console.error(e);
        setIsSelecting(false);
      }
    },
    [isSelecting, leaseApplications, accessToken, sequence, dispatch, navigate],
  );

  return { selectApplication, isSelecting };
};
